import { useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  WalletIcon,
  ClockIcon,
  CheckCircle2Icon,
  XCircleIcon,
  ExternalLinkIcon,
  BanknoteIcon,
} from "lucide-react";
import type { AffiliatePayout } from "../types";
import { AffiliateRequestPayoutModal } from "./affiliate-request-payout-modal";

type AffiliatePayoutHistoryProps = {
  payouts: AffiliatePayout[];
  pendingBalance: number;
  isLoading: boolean;
  affiliateStatus?: string;
};

const statusConfig = {
  pending: {
    label: "Pending",
    icon: ClockIcon,
    className: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  approved: {
    label: "Approved",
    icon: CheckCircle2Icon,
    className: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  rejected: {
    label: "Rejected",
    icon: XCircleIcon,
    className: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  },
  paid: {
    label: "Paid",
    icon: CheckCircle2Icon,
    className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
};

export const AffiliatePayoutHistory = ({
  payouts,
  pendingBalance,
  isLoading,
  affiliateStatus = "active",
}: AffiliatePayoutHistoryProps) => {
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  
  const isSuspended = affiliateStatus === "suspended";

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-muted rounded mb-4" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-muted/50 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Check if there's a pending payout request
  const hasPendingRequest = payouts.some((p) => p.status === "pending");

  return (
    <>
      <div className="rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-border/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold">Payouts</h3>
              <p className="text-xs text-muted-foreground">
                ${formatCurrency(pendingBalance, 2)} available
              </p>
            </div>

            <Button
              onClick={() => setRequestModalOpen(true)}
              disabled={pendingBalance <= 0 || hasPendingRequest || isSuspended}
              size="sm"
              className="gap-2"
            >
              <WalletIcon className="size-4" />
              {isSuspended ? "Payouts Suspended" : hasPendingRequest ? "Request Pending" : "Request Payout"}
            </Button>
          </div>
        </div>

        {/* Payout List */}
        <div className="divide-y divide-border/30">
          {payouts.length === 0 ? (
            <div className="px-4 md:px-6 py-8 text-center text-muted-foreground">
              <BanknoteIcon className="size-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No payouts yet</p>
              <p className="text-xs mt-1">
                Your earnings will appear here when you request a payout
              </p>
            </div>
          ) : (
            payouts.map((payout) => {
              const config = statusConfig[payout.status];
              const StatusIcon = config.icon;

              return (
                <div
                  key={payout.id}
                  className="px-4 md:px-6 py-4 hover:bg-muted/20 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", config.className)}>
                        <StatusIcon className="size-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">
                            ${formatCurrency(payout.amount, 2)}
                          </p>
                          <span
                            className={cn(
                              "px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase",
                              config.className
                            )}
                          >
                            {config.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Requested{" "}
                          {new Date(payout.requestedAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      {payout.status === "paid" && payout.txHash && (
                        <a
                          href={`https://solscan.io/tx/${payout.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          View TX
                          <ExternalLinkIcon className="size-3" />
                        </a>
                      )}
                      {payout.processedAt && (
                        <p className="text-xs text-muted-foreground">
                          {payout.status === "paid" ? "Paid" : "Processed"}{" "}
                          {new Date(payout.processedAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      )}
                      {payout.status === "rejected" && payout.rejectionReason && (
                        <p className="text-xs text-rose-500 max-w-[200px] truncate">
                          {payout.rejectionReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <AffiliateRequestPayoutModal
        open={requestModalOpen}
        onOpenChange={setRequestModalOpen}
        availableBalance={pendingBalance}
      />
    </>
  );
};

